import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { RecordButton } from "./components/record-button";
import { MobileRecordButton } from "./components/mobile-record-button";
import { RecordingControls } from "./components/recording-controls";
import { DebugOverlay } from "./components/debug-overlay";
import { RECORDING_STARTED_EVENT_NAME } from "../../../constants";
import { useMicrophonePermission } from "./hooks/useMicrophonePermission";
import { useAudioRecording } from "./hooks/useAudioRecording";
import { isDebugMode } from "../../../../../utils/env";

import "./audio-recorder.scss";

export interface AudioRecorderProps {
  disabled?: boolean;
  onAudioReady?: (audio: Blob) => Promise<void>;
}

export type RecordingStatus = "inactive" | "active" | "accepted" | "discarded";

export const MINIMAL_RECORDING_TIME = 1; // seconds

export function AudioRecorder({ disabled, onAudioReady }: AudioRecorderProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { hasMicrophoneAccess, isCheckingPermission } =
    useMicrophonePermission();
  const recordingStatusRef = useRef<RecordingStatus>("inactive");
  const isMobile = false; //useIsMobile(); // issue debugging
  const {
    recordingTime,
    startRecording,
    stopRecording,
    isRecording,
    recordingBlob,
  } = useAudioRecording();

  // Debug state for audio playback
  const [debugAudioUrl, setDebugAudioUrl] = useState<string | null>(null);
  const [debugAudioInfo, setDebugAudioInfo] = useState<{
    size: number;
    type: string;
    duration: number;
    recordingStatus: string;
  } | null>(null);

  useEffect(() => {
    const processAudio = async () => {
      setIsLoading(true);

      if (onAudioReady && recordingBlob) {
        await onAudioReady(recordingBlob);
      }

      setIsLoading(false);
    };

    if (recordingBlob && recordingStatusRef.current === "accepted") {
      // Debug: Create URL for the recorded blob
      const url = URL.createObjectURL(recordingBlob);
      setDebugAudioUrl(url);

      // Get audio duration
      const audio = new Audio(url);
      audio.addEventListener("loadedmetadata", () => {
        setDebugAudioInfo({
          size: recordingBlob.size,
          type: recordingBlob.type,
          duration: audio.duration,
          recordingStatus: "accepted",
        });
      });

      // If metadata doesn't load (common on some mobile browsers), still show info
      setTimeout(() => {
        if (!debugAudioInfo) {
          setDebugAudioInfo({
            size: recordingBlob.size,
            type: recordingBlob.type,
            duration: 0,
            recordingStatus: "accepted (metadata pending)",
          });
        }
      }, 500);

      processAudio();
      recordingStatusRef.current = "inactive";
    }
  }, [recordingBlob, onAudioReady, debugAudioInfo]);

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (debugAudioUrl) {
        URL.revokeObjectURL(debugAudioUrl);
      }
    };
  }, [debugAudioUrl]);

  function handleStartRecordingClick() {
    if (disabled || !hasMicrophoneAccess) {
      return;
    }

    startRecording();
    window.dispatchEvent(new CustomEvent(RECORDING_STARTED_EVENT_NAME));
    recordingStatusRef.current = "active";
  }

  function handleDiscardClick() {
    stopRecording();
    recordingStatusRef.current = "discarded";
  }

  async function handleAcceptClick() {
    if (recordingTime > MINIMAL_RECORDING_TIME) {
      stopRecording();
      recordingStatusRef.current = "accepted";
    } else {
      handleDiscardClick();
    }
  }

  function handleCloseDebugOverlay() {
    if (debugAudioUrl) {
      URL.revokeObjectURL(debugAudioUrl);
    }
    setDebugAudioUrl(null);
    setDebugAudioInfo(null);
  }

  return (
    <>
      {/* Debug Overlay */}
      {isDebugMode() && debugAudioUrl && (
        <DebugOverlay
          audioUrl={debugAudioUrl}
          audioInfo={debugAudioInfo}
          onClose={handleCloseDebugOverlay}
        />
      )}

      <div
        className={classNames("audio-recorder-wrapper", {
          "is-recording": isRecording,
          "is-mobile": isMobile,
        })}
      >
        {isMobile ? (
          <>
            <RecordingControls
              disabled={disabled}
              isRecording={isRecording}
              recordingTime={recordingTime}
              onAccept={handleAcceptClick}
              onDiscard={handleDiscardClick}
            />
            <MobileRecordButton
              isRecording={isRecording}
              onStartRecording={handleStartRecordingClick}
              onStopRecording={handleAcceptClick}
              isLoading={isLoading}
              disabled={
                disabled || !hasMicrophoneAccess || isCheckingPermission
              }
            />
          </>
        ) : (
          <>
            <RecordButton
              disabled={
                disabled || !hasMicrophoneAccess || isCheckingPermission
              }
              isLoading={isLoading}
              isRecording={isRecording}
              onStartRecording={handleStartRecordingClick}
            />
            <RecordingControls
              disabled={disabled}
              isRecording={isRecording}
              recordingTime={recordingTime}
              onAccept={handleAcceptClick}
              onDiscard={handleDiscardClick}
            />
          </>
        )}
      </div>
    </>
  );
}
